import { useState, useEffect, useCallback } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Users, IndianRupee, Edit2, Trash2, FileText, Printer, X, Briefcase, Calendar, CreditCard, ChevronDown, ChevronUp, Search } from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';

const MONTHS = [
  '01','02','03','04','05','06','07','08','09','10','11','12'
];
const MONTH_NAMES = {
  '01':'January','02':'February','03':'March','04':'April','05':'May','06':'June',
  '07':'July','08':'August','09':'September','10':'October','11':'November','12':'December'
};

function getMonthLabel(m) {
  if (!m) return '';
  const [y, mm] = m.split('-');
  return `${MONTH_NAMES[mm] || mm} ${y}`;
}

export default function Salary({ user, onLogout }) {
  const [tab, setTab] = useState('employees');
  const [employees, setEmployees] = useState([]);
  const [salaryRecords, setSalaryRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [empDialog, setEmpDialog] = useState(false);
  const [editingEmp, setEditingEmp] = useState(null);
  const [salaryDialog, setSalaryDialog] = useState(false);
  const [editingSalary, setEditingSalary] = useState(null);
  const [slipDialog, setSlipDialog] = useState(false);
  const [slipData, setSlipData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);

  const [empForm, setEmpForm] = useState({
    name: '', mobile: '', designation: '', department: '',
    basic_salary: '', joining_date: '', bank_account: '', bank_name: '', ifsc_code: '', address: ''
  });

  const [salaryForm, setSalaryForm] = useState({
    employee_id: '', month: '', basic_salary: '',
    allowances: [], deductions: [], advance: '', bonus: '',
    status: 'Pending', payment_date: '', payment_mode: '', notes: ''
  });

  const fetchEmployees = useCallback(async () => {
    try {
      const res = await api.get('/employees');
      setEmployees(res.data || []);
    } catch { toast.error('Employees load nahi ho paye'); }
  }, []);

  const fetchSalaryRecords = useCallback(async () => {
    try {
      let url = '/salary-records';
      const params = [];
      if (filterMonth) params.push(`month=${filterMonth}`);
      if (params.length) url += '?' + params.join('&');
      const res = await api.get(url);
      setSalaryRecords(res.data || []);
    } catch { toast.error('Salary records load nahi ho paye'); }
  }, [filterMonth]);

  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);
  useEffect(() => { fetchSalaryRecords(); }, [fetchSalaryRecords]);

  const resetEmpForm = () => setEmpForm({ name: '', mobile: '', designation: '', department: '', basic_salary: '', joining_date: '', bank_account: '', bank_name: '', ifsc_code: '', address: '' });

  const resetSalaryForm = () => setSalaryForm({ employee_id: '', month: '', basic_salary: '', allowances: [], deductions: [], advance: '', bonus: '', status: 'Pending', payment_date: '', payment_mode: '', notes: '' });

  const openAddEmployee = () => { resetEmpForm(); setEditingEmp(null); setEmpDialog(true); };
  const openEditEmployee = (emp) => {
    setEditingEmp(emp);
    setEmpForm({
      name: emp.name || '', mobile: emp.mobile || '', designation: emp.designation || '',
      department: emp.department || '', basic_salary: emp.basic_salary || '',
      joining_date: emp.joining_date || '', bank_account: emp.bank_account || '',
      bank_name: emp.bank_name || '', ifsc_code: emp.ifsc_code || '', address: emp.address || ''
    });
    setEmpDialog(true);
  };

  const handleSaveEmployee = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingEmp) {
        await api.put(`/employees/${editingEmp.id}`, empForm);
        toast.success('Employee updated!');
      } else {
        await api.post('/employees', empForm);
        toast.success('Employee added!');
      }
      setEmpDialog(false);
      fetchEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error saving employee');
    } finally { setLoading(false); }
  };

  const handleDeleteEmployee = async (id) => {
    if (!window.confirm('Kya aap is employee ko delete karna chahte hain?')) return;
    try {
      await api.delete(`/employees/${id}`);
      toast.success('Employee deleted');
      fetchEmployees();
    } catch { toast.error('Delete failed'); }
  };

  const openAddSalary = () => {
    resetSalaryForm();
    setEditingSalary(null);
    setSalaryDialog(true);
  };

  const openEditSalary = (rec) => {
    setEditingSalary(rec);
    setSalaryForm({
      employee_id: rec.employee_id || '', month: rec.month || '',
      basic_salary: rec.basic_salary || '', allowances: rec.allowances || [],
      deductions: rec.deductions || [], advance: rec.advance || '',
      bonus: rec.bonus || '', status: rec.status || 'Pending',
      payment_date: rec.payment_date || '', payment_mode: rec.payment_mode || '', notes: rec.notes || ''
    });
    setSalaryDialog(true);
  };

  const handleSaveSalary = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (editingSalary) {
        await api.put(`/salary-records/${editingSalary.id}`, salaryForm);
        toast.success('Salary record updated!');
      } else {
        await api.post('/salary-records', salaryForm);
        toast.success('Salary record created!');
      }
      setSalaryDialog(false);
      fetchSalaryRecords();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error saving salary');
    } finally { setLoading(false); }
  };

  const handleDeleteSalary = async (id) => {
    if (!window.confirm('Kya aap yeh salary record delete karna chahte hain?')) return;
    try {
      await api.delete(`/salary-records/${id}`);
      toast.success('Record deleted');
      fetchSalaryRecords();
    } catch { toast.error('Delete failed'); }
  };

  const handleMarkPaid = async (rec) => {
    try {
      await api.put(`/salary-records/${rec.id}`, {
        ...rec, status: 'Paid',
        payment_date: new Date().toISOString().split('T')[0],
      });
      toast.success('Salary paid!');
      fetchSalaryRecords();
    } catch { toast.error('Update failed'); }
  };

  const viewSlip = async (rec) => {
    try {
      const res = await api.get(`/salary-records/${rec.id}/slip`);
      setSlipData(res.data);
      setSlipDialog(true);
    } catch { toast.error('Slip load nahi ho payi'); }
  };

  const printSlip = () => {
    const el = document.getElementById('salary-slip-print');
    if (!el) return;
    const w = window.open('', '_blank', 'width=800,height=600');
    w.document.write(`<html><head><title>Salary Slip</title><style>
      body{font-family:Arial,sans-serif;margin:20px;color:#1e293b}
      .slip-container{max-width:700px;margin:0 auto;border:2px solid #0B1120;padding:0}
      .slip-header{background:#0B1120;color:#C9A84C;padding:16px 24px;text-align:center}
      .slip-header h1{margin:0;font-size:20px}
      .slip-header p{margin:4px 0 0;font-size:12px;color:#e2e8f0}
      .slip-body{padding:20px 24px}
      .info-grid{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:16px;font-size:13px}
      .info-grid .label{color:#64748b;font-weight:500}
      .info-grid .value{font-weight:600;color:#1e293b}
      table{width:100%;border-collapse:collapse;margin:12px 0;font-size:13px}
      th{background:#f1f5f9;text-align:left;padding:8px 12px;border:1px solid #e2e8f0;font-weight:600}
      td{padding:8px 12px;border:1px solid #e2e8f0}
      .text-right{text-align:right}
      .total-row{background:#f8fafc;font-weight:700}
      .net-row{background:#0B1120;color:#C9A84C;font-size:15px}
      .net-row td{border-color:#1e293b;padding:10px 12px}
      .footer{margin-top:24px;padding-top:12px;border-top:1px solid #e2e8f0;font-size:11px;color:#94a3b8;text-align:center}
      .sign-area{display:flex;justify-content:space-between;margin-top:40px;font-size:12px}
      .sign-box{text-align:center;min-width:150px}
      .sign-box .line{border-top:1px solid #94a3b8;margin-top:40px;padding-top:4px}
      @media print{body{margin:0}.slip-container{border:2px solid #0B1120}}
    </style></head><body>${el.innerHTML}</body></html>`);
    w.document.close();
    w.print();
  };

  // Employee selection changes auto-fill basic_salary
  const onEmployeeSelect = (empId) => {
    const emp = employees.find(e => e.id === empId);
    setSalaryForm(prev => ({
      ...prev, employee_id: empId,
      basic_salary: emp ? emp.basic_salary : ''
    }));
  };

  // Calculate totals for salary form
  const calcTotals = () => {
    const basic = parseFloat(salaryForm.basic_salary) || 0;
    const allowTotal = salaryForm.allowances.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
    const deductTotal = salaryForm.deductions.reduce((s, d) => s + (parseFloat(d.amount) || 0), 0);
    const advance = parseFloat(salaryForm.advance) || 0;
    const bonus = parseFloat(salaryForm.bonus) || 0;
    return {
      totalEarnings: basic + allowTotal + bonus,
      totalDeductions: deductTotal + advance,
      net: basic + allowTotal + bonus - deductTotal - advance
    };
  };

  const addAllowance = () => setSalaryForm(p => ({ ...p, allowances: [...p.allowances, { label: '', amount: '' }] }));
  const removeAllowance = (i) => setSalaryForm(p => ({ ...p, allowances: p.allowances.filter((_, idx) => idx !== i) }));
  const updateAllowance = (i, field, val) => setSalaryForm(p => {
    const a = [...p.allowances]; a[i] = { ...a[i], [field]: val }; return { ...p, allowances: a };
  });

  const addDeduction = () => setSalaryForm(p => ({ ...p, deductions: [...p.deductions, { label: '', amount: '' }] }));
  const removeDeduction = (i) => setSalaryForm(p => ({ ...p, deductions: p.deductions.filter((_, idx) => idx !== i) }));
  const updateDeduction = (i, field, val) => setSalaryForm(p => {
    const d = [...p.deductions]; d[i] = { ...d[i], [field]: val }; return { ...p, deductions: d };
  });

  const filteredEmployees = searchQuery.trim()
    ? employees.filter(e => (e.name || '').toLowerCase().includes(searchQuery.toLowerCase()) || (e.mobile || '').includes(searchQuery) || (e.designation || '').toLowerCase().includes(searchQuery.toLowerCase()))
    : employees;
  const activeEmployees = employees.filter(e => e.status === 'active');

  const totals = calcTotals();
  const currentYear = new Date().getFullYear();
  const monthOptions = [];
  for (let y = currentYear; y >= currentYear - 1; y--) {
    MONTHS.forEach(m => monthOptions.push({ value: `${y}-${m}`, label: `${MONTH_NAMES[m]} ${y}` }));
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-4 max-w-6xl mx-auto">
        {/* Header */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <CardTitle className="flex items-center gap-2">
                <IndianRupee size={22} className="text-emerald-600" />
                Salary Management
              </CardTitle>
              <div className="flex gap-2">
                <Button variant={tab === 'employees' ? 'default' : 'outline'} onClick={() => setTab('employees')} className={tab === 'employees' ? 'bg-emerald-600 hover:bg-emerald-700' : ''} data-testid="tab-employees">
                  <Users size={16} className="mr-1" /> Employees
                </Button>
                <Button variant={tab === 'salary' ? 'default' : 'outline'} onClick={() => setTab('salary')} className={tab === 'salary' ? 'bg-emerald-600 hover:bg-emerald-700' : ''} data-testid="tab-salary">
                  <CreditCard size={16} className="mr-1" /> Salary Records
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* ========== EMPLOYEES TAB ========== */}
        {tab === 'employees' && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <Input placeholder="Search employee..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-64" data-testid="employee-search" />
                  <span className="text-sm text-slate-500">{filteredEmployees.length} employees</span>
                </div>
                <Button onClick={openAddEmployee} className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="add-employee-btn">
                  <Plus size={16} /> Add Employee
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Designation</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Mobile</TableHead>
                    <TableHead>Basic Salary</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEmployees.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">Koi employee nahi mila</TableCell></TableRow>
                  ) : filteredEmployees.map(emp => (
                    <TableRow key={emp.id} className="hover:bg-slate-50">
                      <TableCell className="font-semibold">{emp.name}</TableCell>
                      <TableCell>{emp.designation || '-'}</TableCell>
                      <TableCell>{emp.department || '-'}</TableCell>
                      <TableCell>{emp.mobile || '-'}</TableCell>
                      <TableCell className="font-semibold text-emerald-700">Rs. {(emp.basic_salary || 0).toLocaleString('en-IN')}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${emp.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                          {emp.status === 'active' ? 'Active' : 'Inactive'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => openEditEmployee(emp)} className="text-blue-600 hover:text-blue-700" data-testid={`edit-emp-${emp.id}`}><Edit2 size={14} /></Button>
                          <Button size="sm" variant="outline" onClick={() => handleDeleteEmployee(emp.id)} className="text-red-600 hover:text-red-700" data-testid={`delete-emp-${emp.id}`}><Trash2 size={14} /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* ========== SALARY RECORDS TAB ========== */}
        {tab === 'salary' && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <Select value={filterMonth} onValueChange={setFilterMonth}>
                    <SelectTrigger className="w-48" data-testid="filter-month"><SelectValue placeholder="All Months" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Months</SelectItem>
                      {monthOptions.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {filterMonth && filterMonth !== 'all' && (
                    <Button variant="ghost" size="sm" onClick={() => setFilterMonth('')}>Clear</Button>
                  )}
                  <span className="text-sm text-slate-500">{salaryRecords.length} records</span>
                </div>
                <Button onClick={openAddSalary} className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="add-salary-btn" disabled={activeEmployees.length === 0}>
                  <Plus size={16} /> Create Salary
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Month</TableHead>
                    <TableHead>Basic</TableHead>
                    <TableHead>Earnings</TableHead>
                    <TableHead>Deductions</TableHead>
                    <TableHead>Net Salary</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {salaryRecords.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">Koi salary record nahi mila</TableCell></TableRow>
                  ) : salaryRecords.map(rec => (
                    <TableRow key={rec.id} className="hover:bg-slate-50" data-testid={`salary-row-${rec.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-semibold">{rec.employee_name}</p>
                          <p className="text-xs text-slate-500">{rec.designation}</p>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{getMonthLabel(rec.month)}</TableCell>
                      <TableCell>Rs. {(rec.basic_salary || 0).toLocaleString('en-IN')}</TableCell>
                      <TableCell className="text-emerald-700 font-semibold">Rs. {(rec.total_earnings || 0).toLocaleString('en-IN')}</TableCell>
                      <TableCell className="text-red-600">Rs. {(rec.total_deductions || 0).toLocaleString('en-IN')}</TableCell>
                      <TableCell className="font-bold text-lg">Rs. {(rec.net_salary || 0).toLocaleString('en-IN')}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${rec.status === 'Paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                          {rec.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" onClick={() => viewSlip(rec)} className="text-emerald-600 hover:text-emerald-700 gap-1" data-testid={`view-slip-${rec.id}`}><FileText size={14} /> Slip</Button>
                          {rec.status !== 'Paid' && (
                            <Button size="sm" variant="outline" onClick={() => handleMarkPaid(rec)} className="text-blue-600 hover:text-blue-700 gap-1" data-testid={`mark-paid-${rec.id}`}><CreditCard size={14} /> Pay</Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => openEditSalary(rec)} className="text-slate-600" data-testid={`edit-salary-${rec.id}`}><Edit2 size={14} /></Button>
                          <Button size="sm" variant="outline" onClick={() => handleDeleteSalary(rec.id)} className="text-red-600" data-testid={`delete-salary-${rec.id}`}><Trash2 size={14} /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* ========== ADD/EDIT EMPLOYEE DIALOG ========== */}
        <Dialog open={empDialog} onOpenChange={setEmpDialog}>
          <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingEmp ? 'Edit Employee' : 'Add New Employee'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSaveEmployee} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Name *</Label>
                  <Input value={empForm.name} onChange={e => setEmpForm({ ...empForm, name: e.target.value })} required data-testid="emp-name" />
                </div>
                <div className="space-y-1">
                  <Label>Mobile</Label>
                  <Input value={empForm.mobile} onChange={e => setEmpForm({ ...empForm, mobile: e.target.value })} data-testid="emp-mobile" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Designation</Label>
                  <Input value={empForm.designation} onChange={e => setEmpForm({ ...empForm, designation: e.target.value })} placeholder="e.g. Manager, Accountant" data-testid="emp-designation" />
                </div>
                <div className="space-y-1">
                  <Label>Department</Label>
                  <Input value={empForm.department} onChange={e => setEmpForm({ ...empForm, department: e.target.value })} placeholder="e.g. Sales, HR" data-testid="emp-department" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Basic Salary (Monthly)</Label>
                  <Input type="number" value={empForm.basic_salary} onChange={e => setEmpForm({ ...empForm, basic_salary: e.target.value })} data-testid="emp-basic-salary" />
                </div>
                <div className="space-y-1">
                  <Label>Joining Date</Label>
                  <Input type="date" value={empForm.joining_date} onChange={e => setEmpForm({ ...empForm, joining_date: e.target.value })} data-testid="emp-joining-date" />
                </div>
              </div>
              <div className="pt-2 border-t">
                <Label className="text-sm font-semibold text-slate-600 mb-2 block">Bank Details</Label>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Bank Name</Label>
                    <Input value={empForm.bank_name} onChange={e => setEmpForm({ ...empForm, bank_name: e.target.value })} data-testid="emp-bank-name" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Account No.</Label>
                    <Input value={empForm.bank_account} onChange={e => setEmpForm({ ...empForm, bank_account: e.target.value })} data-testid="emp-bank-account" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">IFSC</Label>
                    <Input value={empForm.ifsc_code} onChange={e => setEmpForm({ ...empForm, ifsc_code: e.target.value })} data-testid="emp-ifsc" />
                  </div>
                </div>
              </div>
              <div className="space-y-1">
                <Label>Address</Label>
                <Input value={empForm.address} onChange={e => setEmpForm({ ...empForm, address: e.target.value })} data-testid="emp-address" />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-employee-btn">
                {loading ? 'Saving...' : editingEmp ? 'Update Employee' : 'Add Employee'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* ========== ADD/EDIT SALARY DIALOG ========== */}
        <Dialog open={salaryDialog} onOpenChange={setSalaryDialog}>
          <DialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingSalary ? 'Edit Salary Record' : 'Create Salary Record'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSaveSalary} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Employee *</Label>
                  <Select value={salaryForm.employee_id} onValueChange={onEmployeeSelect} disabled={!!editingSalary}>
                    <SelectTrigger data-testid="salary-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                    <SelectContent>
                      {activeEmployees.map(e => <SelectItem key={e.id} value={e.id}>{e.name} - {e.designation || 'N/A'}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Month *</Label>
                  <Select value={salaryForm.month} onValueChange={v => setSalaryForm({ ...salaryForm, month: v })} disabled={!!editingSalary}>
                    <SelectTrigger data-testid="salary-month-select"><SelectValue placeholder="Select month" /></SelectTrigger>
                    <SelectContent>
                      {monthOptions.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label>Basic Salary</Label>
                  <Input type="number" value={salaryForm.basic_salary} onChange={e => setSalaryForm({ ...salaryForm, basic_salary: e.target.value })} data-testid="salary-basic" />
                </div>
                <div className="space-y-1">
                  <Label>Bonus</Label>
                  <Input type="number" value={salaryForm.bonus} onChange={e => setSalaryForm({ ...salaryForm, bonus: e.target.value })} data-testid="salary-bonus" />
                </div>
                <div className="space-y-1">
                  <Label>Advance Deduction</Label>
                  <Input type="number" value={salaryForm.advance} onChange={e => setSalaryForm({ ...salaryForm, advance: e.target.value })} data-testid="salary-advance" />
                </div>
              </div>

              {/* Allowances */}
              <div className="border rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-semibold text-emerald-700">Allowances</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addAllowance} className="text-emerald-600 gap-1" data-testid="add-allowance-btn"><Plus size={12} /> Add</Button>
                </div>
                {salaryForm.allowances.map((a, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <Input placeholder="Label (e.g. HRA, DA)" value={a.label} onChange={e => updateAllowance(i, 'label', e.target.value)} className="flex-1" />
                    <Input type="number" placeholder="Amount" value={a.amount} onChange={e => updateAllowance(i, 'amount', e.target.value)} className="w-32" />
                    <Button type="button" size="sm" variant="ghost" onClick={() => removeAllowance(i)} className="text-red-500"><X size={14} /></Button>
                  </div>
                ))}
                {salaryForm.allowances.length === 0 && <p className="text-xs text-slate-400">Koi allowance nahi</p>}
              </div>

              {/* Deductions */}
              <div className="border rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-semibold text-red-600">Deductions</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addDeduction} className="text-red-600 gap-1" data-testid="add-deduction-btn"><Plus size={12} /> Add</Button>
                </div>
                {salaryForm.deductions.map((d, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <Input placeholder="Label (e.g. PF, ESI, TDS)" value={d.label} onChange={e => updateDeduction(i, 'label', e.target.value)} className="flex-1" />
                    <Input type="number" placeholder="Amount" value={d.amount} onChange={e => updateDeduction(i, 'amount', e.target.value)} className="w-32" />
                    <Button type="button" size="sm" variant="ghost" onClick={() => removeDeduction(i)} className="text-red-500"><X size={14} /></Button>
                  </div>
                ))}
                {salaryForm.deductions.length === 0 && <p className="text-xs text-slate-400">Koi deduction nahi</p>}
              </div>

              {/* Summary */}
              <div className="bg-slate-50 rounded-lg p-3 grid grid-cols-3 gap-3 text-sm">
                <div><span className="text-slate-500">Total Earnings:</span><p className="font-bold text-emerald-700">Rs. {totals.totalEarnings.toLocaleString('en-IN')}</p></div>
                <div><span className="text-slate-500">Total Deductions:</span><p className="font-bold text-red-600">Rs. {totals.totalDeductions.toLocaleString('en-IN')}</p></div>
                <div><span className="text-slate-500">Net Salary:</span><p className="font-bold text-lg">Rs. {totals.net.toLocaleString('en-IN')}</p></div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Payment Mode</Label>
                  <Select value={salaryForm.payment_mode} onValueChange={v => setSalaryForm({ ...salaryForm, payment_mode: v })}>
                    <SelectTrigger><SelectValue placeholder="Select mode" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Payment Date</Label>
                  <Input type="date" value={salaryForm.payment_date} onChange={e => setSalaryForm({ ...salaryForm, payment_date: e.target.value })} />
                </div>
              </div>

              <div className="space-y-1">
                <Label>Notes</Label>
                <Input value={salaryForm.notes} onChange={e => setSalaryForm({ ...salaryForm, notes: e.target.value })} placeholder="Optional notes" />
              </div>

              <Button type="submit" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-salary-btn">
                {loading ? 'Saving...' : editingSalary ? 'Update Record' : 'Create Salary Record'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* ========== SALARY SLIP DIALOG ========== */}
        <Dialog open={slipDialog} onOpenChange={setSlipDialog}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span>Salary Slip</span>
                <Button size="sm" onClick={printSlip} className="bg-emerald-600 hover:bg-emerald-700 gap-1" data-testid="print-slip-btn"><Printer size={14} /> Print</Button>
              </DialogTitle>
            </DialogHeader>
            {slipData && (
              <div id="salary-slip-print">
                <div className="slip-container" style={{ maxWidth: 700, margin: '0 auto', border: '2px solid #0B1120' }}>
                  {/* Header */}
                  <div style={{ background: '#0B1120', color: '#C9A84C', padding: '16px 24px', textAlign: 'center' }}>
                    <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>GANESA HOMESTATE DEVELOPERS</h1>
                    <p style={{ margin: '4px 0 0', fontSize: 12, color: '#e2e8f0' }}>Salary Slip - {getMonthLabel(slipData.record.month)}</p>
                  </div>
                  {/* Employee Info */}
                  <div style={{ padding: '16px 24px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 13, borderBottom: '1px solid #e2e8f0' }}>
                    <div><span style={{ color: '#64748b' }}>Employee Name:</span> <strong>{slipData.employee.name}</strong></div>
                    <div><span style={{ color: '#64748b' }}>Designation:</span> <strong>{slipData.employee.designation || '-'}</strong></div>
                    <div><span style={{ color: '#64748b' }}>Department:</span> <strong>{slipData.employee.department || '-'}</strong></div>
                    <div><span style={{ color: '#64748b' }}>Month:</span> <strong>{getMonthLabel(slipData.record.month)}</strong></div>
                    {slipData.employee.bank_name && <div><span style={{ color: '#64748b' }}>Bank:</span> <strong>{slipData.employee.bank_name}</strong></div>}
                    {slipData.employee.bank_account && <div><span style={{ color: '#64748b' }}>Account:</span> <strong>{slipData.employee.bank_account}</strong></div>}
                  </div>
                  {/* Earnings & Deductions Table */}
                  <div style={{ padding: '12px 24px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr>
                          <th style={{ background: '#f1f5f9', textAlign: 'left', padding: '8px 12px', border: '1px solid #e2e8f0', width: '50%' }}>Earnings</th>
                          <th style={{ background: '#f1f5f9', textAlign: 'right', padding: '8px 12px', border: '1px solid #e2e8f0' }}>Amount</th>
                          <th style={{ background: '#f1f5f9', textAlign: 'left', padding: '8px 12px', border: '1px solid #e2e8f0', width: '30%' }}>Deductions</th>
                          <th style={{ background: '#f1f5f9', textAlign: 'right', padding: '8px 12px', border: '1px solid #e2e8f0' }}>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>Basic Salary</td>
                          <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', textAlign: 'right' }}>Rs. {(slipData.record.basic_salary || 0).toLocaleString('en-IN')}</td>
                          <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>{(slipData.record.deductions || [])[0]?.label || '-'}</td>
                          <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', textAlign: 'right' }}>{(slipData.record.deductions || [])[0] ? `Rs. ${parseFloat((slipData.record.deductions[0]).amount || 0).toLocaleString('en-IN')}` : '-'}</td>
                        </tr>
                        {(slipData.record.allowances || []).map((a, i) => (
                          <tr key={`a-${i}`}>
                            <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>{a.label}</td>
                            <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', textAlign: 'right' }}>Rs. {parseFloat(a.amount || 0).toLocaleString('en-IN')}</td>
                            <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>{(slipData.record.deductions || [])[i + 1]?.label || ''}</td>
                            <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', textAlign: 'right' }}>{(slipData.record.deductions || [])[i + 1] ? `Rs. ${parseFloat((slipData.record.deductions[i + 1]).amount || 0).toLocaleString('en-IN')}` : ''}</td>
                          </tr>
                        ))}
                        {slipData.record.bonus > 0 && (
                          <tr>
                            <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>Bonus</td>
                            <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', textAlign: 'right' }}>Rs. {(slipData.record.bonus || 0).toLocaleString('en-IN')}</td>
                            <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>{slipData.record.advance > 0 ? 'Advance' : ''}</td>
                            <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', textAlign: 'right' }}>{slipData.record.advance > 0 ? `Rs. ${(slipData.record.advance || 0).toLocaleString('en-IN')}` : ''}</td>
                          </tr>
                        )}
                        {slipData.record.bonus <= 0 && slipData.record.advance > 0 && (
                          <tr>
                            <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}></td>
                            <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', textAlign: 'right' }}></td>
                            <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>Advance</td>
                            <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', textAlign: 'right' }}>Rs. {(slipData.record.advance || 0).toLocaleString('en-IN')}</td>
                          </tr>
                        )}
                        {/* Totals */}
                        <tr style={{ background: '#f8fafc', fontWeight: 700 }}>
                          <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>Total Earnings</td>
                          <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', textAlign: 'right' }}>Rs. {(slipData.record.total_earnings || 0).toLocaleString('en-IN')}</td>
                          <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0' }}>Total Deductions</td>
                          <td style={{ padding: '8px 12px', border: '1px solid #e2e8f0', textAlign: 'right' }}>Rs. {(slipData.record.total_deductions || 0).toLocaleString('en-IN')}</td>
                        </tr>
                      </tbody>
                    </table>
                    {/* Net Salary */}
                    <div style={{ background: '#0B1120', color: '#C9A84C', padding: '12px 16px', marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 16, fontWeight: 700 }}>
                      <span>Net Salary</span>
                      <span>Rs. {(slipData.record.net_salary || 0).toLocaleString('en-IN')}</span>
                    </div>
                    {/* Payment Info */}
                    {slipData.record.payment_mode && (
                      <div style={{ marginTop: 12, fontSize: 12, color: '#64748b' }}>
                        <strong>Payment:</strong> {slipData.record.payment_mode?.replace('_', ' ')} {slipData.record.payment_date && `| Date: ${slipData.record.payment_date}`}
                      </div>
                    )}
                    {/* Signature */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 48, fontSize: 12 }}>
                      <div style={{ textAlign: 'center', minWidth: 150 }}><div style={{ borderTop: '1px solid #94a3b8', marginTop: 40, paddingTop: 4 }}>Employee Signature</div></div>
                      <div style={{ textAlign: 'center', minWidth: 150 }}><div style={{ borderTop: '1px solid #94a3b8', marginTop: 40, paddingTop: 4 }}>Authorized Signature</div></div>
                    </div>
                    <div style={{ marginTop: 20, paddingTop: 8, borderTop: '1px solid #e2e8f0', fontSize: 10, color: '#94a3b8', textAlign: 'center' }}>
                      This is a computer-generated salary slip. | Ganesa Homestate Developers
                    </div>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
